<template>
  <div class="f-h mgt-problems">
    <FiltersZone :modal-title="$t('message.OPTIONS')">
      <Switcher
        slot="shortcuts"
        v-model="selectedRange"
        class="mgt-problems__switcher"
        :titles="ranges"
      />

      <button class="generic-border generic-border--text-btn generic-border--blue-border" @click="excelExport">
        <Icon>{{ downloadMedium }}</Icon>
        <span>{{ $t('message.EXPORT_TO_EXCEL') }}</span>
      </button>
      <div class="field-hint mt5em mb10em">
        {{ $t('message.PB_EXPORT_ALL') }}
      </div>
      <h2 class="basic-sub-title">
        {{ $t('message.FILTERS') }}
      </h2>
      <ComponentsList :value="mgtPagesOptions" @edit="paramsEdit" />
    </FiltersZone>
    <div class="f-h-below-title mgt-problems__content">
      <div class="mgt-problems__col-headers v-centerer">
        <SortOption class="mgt-problems__col-headers__team" :label="$t('message.TEAM')" />
        <SortOption class="mgt-problems__col-headers__summary" :label="$t('message.SUMMARY')" />
        <SortOption
          v-for="index in 4"
          :key="index"
          class="mgt-problems__col-headers__state"
          :label="$t('message.PB_STATES')[index - 1]"
        />
      </div>
      <div
        v-for="team in teamsData"
        :key="team.id"
        class="v-centerer mgt-problems__item"
        :class="{ selected: teamSelected && team.id === teamSelected.id }"
        @click="teamSelected = (teamSelected && team.id === teamSelected.id) ? null : team"
      >
        <div class="mgt-problems__item__team">
          {{ teamsPerId[`${team.id}`].name }}
        </div>
        <div class="mgt-problems__item__summary h-v-centerer">
          <div>{{ $tc("message.NB_REPORTED_PB", team.reported.length, { nb: team.reported.length }) }}</div>
          <div>{{ $tc("message.NB_COMPLETED_PB", team.completed.length, { nb: team.completed.length }) }}</div>
        </div>
        <div v-for="(state, index) in team.states" :key="index" class="mgt-problems__item__state h-v-centerer">
          <Badge @click.stop.native="selectTeamAndState(team, index)">
            <span slot="badge">{{ state.items.length }}</span>
            <div class="generic-border generic-border--large-icon-button generic-border--gray-border">
              <Icon>{{ ongoingPbIcons[index] }}</Icon>
            </div>
          </Badge>
        </div>
      </div>
    </div>

    <Modal :value="teamSelected != null" class="mgt-problems__selected-team-modal" @input="unselectTeam">
      <div v-if="teamSelected != null" ref="modalWrapper" class="mgt-problems__selected-team-modal__wrapper">
        <h2 class="basic-sub-title mgt-problems__selected-team-modal__title">
          {{ teamsPerId[`${teamSelected.id}`].name }}
        </h2>
        <ExpandableSettingsItem
          v-for="(state, index) in teamSelected.states"
          ref="modalStates"
          :key="index"
          :title="`${$t('message.PB_STATES')[index]} \
            ${$t('message.CONTENT_BETWEEN_PARENTHESES', { content: state.items.length })}`"
          :default-expand="index === stateSelected"
        >
          <div>
            <div v-for="pb in state.items" :key="pb.id">
              <ProblemItem :key="pb.id" :problem="pb" />
            </div>
          </div>
        </ExpandableSettingsItem>
      </div>
    </Modal>
  </div>
</template>

<script>
/**
 * Management page for problems synthesis.
 *
 * As a management page, MgtProblems uses selectedTeamIds from mixins/mgt-pages-teams
 * to filter its content.
 */

import $ from 'jquery';
import toastr from 'toastr';
import { isEqual } from 'lodash';
// Components
import Badge from '@/components/badge.vue';
import ComponentsList from '@/components/components-list.vue';
import ExpandableSettingsItem from '@/components/expandable-settings-item.vue';
import FiltersZone from '@/components/filters-zone.vue';
import Icon from '@/components/icon.vue';
import Modal from '@/components/modal.vue';
import ProblemItem from '@/components/problem-item.vue';
import SortOption from '@/components/sort-option.vue';
import Switcher from '@/components/switcher.vue';
// Mixins
import mixinMgtPagesOptions from '@/mixins/page-options/mgt-pages.vue';
import mixinMgtPagesTeams from '@/mixins/mgt-pages-teams.vue';
// Utils
import {
  clockMedium,
  downloadMedium,
  ongoing,
  sheetAnalysis,
  tickMedium,
} from '@/assets/icons';
import { exportListToExcel } from '@/utils/problem-utils';

export default {

  name: 'MgtProblems',

  components: {
    Badge,
    ComponentsList,
    ExpandableSettingsItem,
    FiltersZone,
    Icon,
    Modal,
    ProblemItem,
    SortOption,
    Switcher,
  },

  mixins: [mixinMgtPagesOptions, mixinMgtPagesTeams],

  data() {
    return {
      downloadMedium,
      teamsData: [],
      stateSelected: -1,
      keepTeamSelected: null,
      ranges: [this.$t('message.SEVEN_DAYS'), this.$t('message.FOUR_WEEKS')],
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.PROBLEMS'),
    };
  },

  computed: {
    /** Array: Icons for problem states */
    ongoingPbIcons() {
      return [
        clockMedium,
        sheetAnalysis,
        ongoing,
        tickMedium,
      ];
    },
    /** Object: With 'start' and 'end' moment instances */
    dateRange() {
      return {
        start: this.selectedRange === 0 ? this.$moment().subtract(7, 'day') : this.$moment().subtract(4, 'week'),
        end: this.$moment(),
      };
    },
    /** Array: Array of the selected teams, defaults to teams managed by current team */
    mgtPagesOptTeams() {
      return this.selectedTeamIds;
    },
    // Query params
    /** Number: Value of the switcher (index of ranges) */
    selectedRange: {
      get() {
        return Number(this.$route.query.mgtProblemsRangeIndex || 0);
      },
      set(range) {
        const query = Object.assign({}, this.$route.query);
        query.mgtProblemsRangeIndex = range;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Object: Team data currently focused or null */
    teamSelected: {
      get() {
        return (this.teamsData || []).find(team => team.id === Number(this.$route.query.mgtProblemsTeam));
      },
      set(team) {
        const query = Object.assign({}, this.$route.query);
        query.mgtProblemsTeam = team === null ? undefined : String(team.id);
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
  },

  watch: {
    isPbSheetDisplayed(newVal) {
      if (newVal) {
        this.keepTeamSelected = this.teamSelected;
        this.teamSelected = null;
      } else {
        this.teamSelected = this.keepTeamSelected;
        this.keepTeamSelected = null;
      }
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    /** Display the problems of the selected team */
    selectTeamAndState(team, state) {
      this.teamSelected = team;
      this.stateSelected = state;
      // Smooth scroll to the related category
      this.$nextTick(() => {
        const { modalWrapper, modalStates } = this.$refs;
        if (modalWrapper && modalStates) {
          $(modalWrapper).animate({ scrollTop: modalStates[state].$el.offsetTop - 5 }, 500);
        }
      });
    },
    unselectTeam() {
      this.teamSelected = null;
      this.stateSelected = -1;
    },
    /** Reload the informations content */
    refresh() {
      const cRange = Object.assign({}, this.dateRange);
      cRange.start = cRange.start.format('YYYY-MM-DD HH:mm:ss');
      cRange.end = cRange.end.format('YYYY-MM-DD HH:mm:ss');
      this.$http.post('/server/management/getProblems.php', { teams: this.selectedTeamIds, range: cRange })
        .then((response) => {
          if (response.data && Array.isArray(response.data)) {
            this.teamsData = response.data;
          }
        });
    },
    /** Replace GET parameters in query when componentsList is edited */
    paramsEdit(option) {
      const query = Object.assign({}, this.$route.query);
      if (option.key === 'teams') {
        query.mgtTeams = option.value.join(',');
      }
      this.$emit('input', option);
      if (!isEqual(query, this.$route.query)) {
        this.$router.replace({ query });
      }
    },
    /** Fetch all the problems and export them in a readable format */
    excelExport() {
      this.$http.get(`/server/problems/export?teamIds=${JSON.stringify(this.selectedTeamIds)}`).then((response) => {
        if (!response || !(response.data instanceof Array)) {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
          return;
        }
        exportListToExcel(response.data);
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.mgt-problems {

  &__switcher {
    height: 2.2rem;
  }

  &__content {
    position: relative;
    overflow: auto;
  }

  &__col-headers {
    margin-top: $page-content-padding;
    height: 1.8em;

    & &__team {
      margin-left: 0.7rem;
      flex: 0 0 10rem;
      justify-content: unset;
    }

    &__summary {
      flex: 1;
    }

    &__state {
      flex: 0 0 8.5rem;
    }
  }

  &__item {
    margin-top: $page-content-padding;
    cursor: pointer;
    background: white;
    border-radius: $main-border-radius;
    height: $main-tile-height;

    &.selected {
      background: rgba(42, 90, 145, 0.15);
    }

    & &__team {
      margin-left: 0.7rem;
      flex: 0 0 10rem;
      justify-content: unset;
      font-weight: 600;
    }

    &__summary {
      flex: 1;
      flex-flow: column;
    }

    &__state {
      flex: 0 0 8.5rem;

      .badge__badge {
        padding: 0.15rem;
        box-sizing: border-box;
        width: auto;
        min-width: 1.3rem;
        border-radius: $high-border-radius;
      }
    }
  }

  &__selected-team-modal {
    &__wrapper {
      width: 100%;
      height: 100%;
      overflow: auto;
      background: $color-body-background;
      padding: $page-content-padding;
      box-sizing: border-box;
    }

    &__title.basic-sub-title {
      margin-top: 0.5rem;
      margin-left: 0.5rem;
    }

    .problem-item {
      border: 1px solid $color-gray-border;
    }
  }
}


@media (max-width: $smartphone-landscape) {
  .mgt-problems {
    &__col-headers {
      &__state {
        flex: 0 0 6.5rem;
      }
    }

    &__item {
      &__state {
        flex: 0 0 6.5rem;
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .mgt-problems {
    &__col-headers {
      & &__team {
        flex: 1;
      }

      &__summary {
        flex: 0 0 6.5rem;
      }

      &__state {
        flex: 0 0 4.5rem;
      }
    }

    &__item {
      & &__team {
        flex: 1;
      }

      &__summary {
        flex: 0 0 6.5rem;
      }

      &__state {
        flex: 0 0 4.5rem;
      }
    }
  }
}
</style>
